import { StandardEvents } from '@shopify/events';

/**
 * HydroWata first-order gift guard (agent F, 11/10/2026). Keeps the FREE gift lines of the bottle offer equal to what
 * the cart earns, after EVERY cart change (buy box, drawer, /cart page, other apps) and on page load.
 *
 * Rule table = agent B's snippets/hw-product-deals.liquid (mode 'json'), printed by snippets/hw-cart.liquid as
 * <script type="application/json" data-hw-cart-deals>. `entitled()` below is a copy of B's function in
 * assets/hw-product.js — change both together (and the Flow check, SECTIONS-B.md).
 *
 * Owner standard (shopify-bundle-flow gift-rules.md, 29–30/09/2026):
 *  #1 gifts = entitled(paid bottles), hung under a bottle line (parent_line_key);
 *  #3 a gift the customer deletes stays deleted (memory per cart token) until bottles are added again: owner
 *     12/10/2026 "quà tặng cho khách cứ add mới là có" - any new add brings back every gift the cart earns;
 *  #5 lowering bottles trims gifts above the new entitlement (never charged: the BXGY tablets would cost $44.99
 *     with 3 bottles, because Shopify then applies the 3-bottle deal instead);
 *  #7 above the top tier gifts scale by combo (B's entitled()).
 * Not done here (report): #2/#4 (gift + → paid twin / re-added real item → gift) — gift rows have no stepper.
 * Every write goes through window.HWCart.queue (assets/hw-cart.js), so it never runs next to another cart write.
 */
if (!window.HWGiftGuard) {
  const MEM = 'hw-gift-mem-v1';
  const SOURCE = 'hw-cart-guard';

  const readRules = () => {
    const el = document.querySelector('script[data-hw-cart-deals]');
    try {
      return el ? JSON.parse(el.textContent || 'null') : null;
    } catch (e) {
      return null;
    }
  };

  // copy of agent B's entitled() (assets/hw-product.js): split into tiers biggest first, add each set's gifts;
  // perOrder gifts at most that many; a gift with max > 0 only counts while bottles <= max
  function entitled(rules, bottles) {
    const out = {};
    if (!rules || !rules.deal || bottles < 1) return out;
    const top = Math.max(...rules.tiers.map((t) => t.qty));
    const sets = [];
    let left = bottles;
    while (left > 0) {
      const q = Math.min(left, top);
      sets.push(q);
      left -= q;
    }
    for (const g of rules.gifts) {
      if (g.max > 0 && bottles > g.max) continue;
      let n = 0;
      for (const q of sets) if (q >= g.unlock) n++;
      if (g.perOrder > 0) n = Math.min(n, g.perOrder);
      n *= g.each || 1; // 21/10/2026: units per set; rows sharing a gift variant add up (case + case2)
      if (n > 0) { if (out[g.variant]) out[g.variant].qty += n; else out[g.variant] = { qty: n, key: g.key, handle: g.handle }; }
    }
    return out;
  }

  const loadMem = (token) => {
    try {
      const m = JSON.parse(sessionStorage.getItem(MEM) || 'null');
      return m && m.token === token ? m : { token, pairs: {} };
    } catch (e) {
      return { token, pairs: {} };
    }
  };
  const saveMem = (m) => {
    try {
      sessionStorage.setItem(MEM, JSON.stringify(m));
    } catch (e) {
      /* private mode */
    }
  };

  /** What to change so the gift lines match the entitlement. */
  function plan(cart, rules) {
    const main = rules.main.handle;
    const giftIds = new Set(rules.gifts.map((g) => String(g.variant)));
    const items = cart.items || [];
    const bottles = items
      .filter((i) => i.handle === main && !giftIds.has(String(i.variant_id)))
      .reduce((a, i) => a + i.quantity, 0);
    const earn = entitled(rules, bottles);
    const mem = loadMem(cart.token);
    if (!items.length) mem.pairs = {};
    const lower = {};
    const add = [];
    // the bottle line that carries the gifts: one already holding gifts, else the first top-level bottle line
    const tops = items.filter((i) => i.handle === main && !i.parent_relationship);
    const holder =
      tops.find((t) => items.some((c) => c.parent_relationship && c.parent_relationship.parent_key === t.key)) || tops[0];

    const seen = new Set();
    for (const g of rules.gifts) {
      const vid = String(g.variant);
      // 21/10/2026: several rows can share one gift variant (case + case2); entitled() already summed them
      if (seen.has(vid)) continue;
      seen.add(vid);
      const lines = items.filter((i) => String(i.variant_id) === vid);
      const held = lines.reduce((a, i) => a + i.quantity, 0);
      const e = earn[vid] ? earn[vid].qty : 0;
      const m = mem.pairs[vid];
      // 12/10/2026: bottles went up since the last check (a new add) -> fill up to everything earned, deleted or not
      const grew = Boolean(m && m.b !== undefined && bottles > m.b);
      let keep = held;
      if (held > e) {
        // trim, keyed by the gift VARIANT: it sets the variant's total (several line keys of one variant in one
        // update are applied one after the other and miss — measured 11/10/2026)
        lower[vid] = e;
        keep = e;
      } else if (held < e && holder) {
        const deleted = !grew && m && (m.del || (m.e === e && held < m.h));
        if (!deleted) {
          // first look at this cart: fill up; afterwards only what was newly earned since the last check
          const n = m && !grew ? Math.min(e - held, Math.max(0, e - m.e)) : e - held;
          if (n > 0) {
            add.push({ id: +vid, quantity: n });
            keep = held + n;
          }
        }
      }
      const del = !grew && Boolean(m && (m.del || (m.e === e && held < m.h && held < e)));
      // the customer added the gift again by hand → no longer "deleted"
      mem.pairs[vid] = { e, h: keep, del: del && !(m && held > m.h), b: bottles };
    }
    return { lower, add, holderVariant: holder && holder.variant_id, mem };
  }

  let timer = 0;
  /** @type {Promise<void>} */
  let running = Promise.resolve();

  const check = () => {
    const rules = readRules();
    const C = window.HWCart;
    if (!rules || !rules.deal || !rules.gifts || !rules.gifts.length || !C) return Promise.resolve();
    running = C.queue(async () => {
      const cart = await C.get();
      const p = plan(cart, rules);
      const hasLower = Object.keys(p.lower).length > 0;
      if (!hasLower && !p.add.length) {
        saveMem(p.mem);
        return null;
      }
      let fresh = cart;
      if (hasLower) fresh = await C.post(Theme.routes.cart_update_url, { updates: p.lower }, !p.add.length);
      if (p.add.length) {
        // keys change after edits: take the holder's key from the freshest cart
        const holder =
          (fresh.items || []).find((i) => i.variant_id === p.holderVariant && !i.parent_relationship) || null;
        const items = p.add.map((a) => (holder ? { ...a, parent_line_key: holder.key } : a));
        await C.post(`${Theme.routes.cart_add_url}`, { items }, false);
        fresh = await C.post(Theme.routes.cart_update_url, { updates: {} }, true);
      }
      saveMem(p.mem);
      C.announce(fresh, { source: SOURCE });
      return fresh;
    }).catch((e) => console.warn('[hw-cart-guard]', e));
    return running;
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = window.setTimeout(check, 80);
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const p = /** @type {any} */ (event).promise;
    if (!p || typeof p.then !== 'function') return;
    p.then(
      (r) => {
        if (r && r.detail && r.detail.source === SOURCE) return;
        schedule();
      },
      () => {}
    );
  });

  window.HWGiftGuard = { check, entitled, plan, rules: readRules, idle: () => running };
  window.HW = Object.assign(window.HW || {}, { giftGuard: window.HWGiftGuard });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
}
